import React, { useState, useEffect, useMemo } from 'react';
import { Stack, Title, Text, Select, Group, Button, Paper, Loader, Center } from '@mantine/core';
import { IconBuildingHospital, IconDownload, IconCalendar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ClinicSeatBillingApi } from '../../../global/apis/ClinicSeatBillingApi/ClinicSeatBillingApi';

/**
 * ClinicSeatBillingTab - Simplified clinic seat billing management
 * Shows a list of current and past months with download buttons for CSV export
 */
const ClinicSeatBillingTab: React.FC = () => {
    const { t } = useTranslation();
    const [monthsToShow, setMonthsToShow] = useState('6');
    const [downloadingMonths, setDownloadingMonths] = useState<Set<string>>(new Set());
    const [monthsWithData, setMonthsWithData] = useState<Set<string>>(new Set());
    const [checkingData, setCheckingData] = useState(false);
    const clinicSeatBillingApi = useMemo(() => new ClinicSeatBillingApi(), []);

    // Generate list of current and past months based on selected number
    const generateRecentMonths = (numMonths: number) => {
        const months = [];
        const currentDate = new Date();
        
        for (let i = 1; i <= numMonths; i++) {
            const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
            const period = monthDate.toISOString().slice(0, 7); // YYYY-MM format
            const displayName = monthDate.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long' 
            });
            
            months.push({
                period,
                displayName,
                year: monthDate.getFullYear(),
                month: monthDate.getMonth() + 1
            });
        }
        
        return months;
    };

    const recentMonths = generateRecentMonths(parseInt(monthsToShow));

    // Effect to check data availability when months change
    useEffect(() => {
        const checkDataAvailability = async (months: any[]) => {
            setCheckingData(true);
            const monthsWithDataSet = new Set<string>();
            
            try {
                // Check each month for data availability in parallel
                const dataChecks = months.map(async (month) => {
                    try {
                        const reportResponse = await clinicSeatBillingApi.getBillingReport(month.period);
                        if (reportResponse.report.clinics && reportResponse.report.clinics.length > 0) 
                            return month.period;
                        
                    } catch {
                        // If there's an error (like 400 for no data), this month doesn't have data
                        console.log(`No data available for period ${month.period}`);
                    }
                    return null;
                });

                const results = await Promise.all(dataChecks);
                results.forEach(period => {
                    if (period) 
                        monthsWithDataSet.add(period);
                    
                });
            } catch (error) {
                console.error('Error checking data availability:', error);
            } finally {
                setMonthsWithData(monthsWithDataSet);
                setCheckingData(false);
            }
        };

        const months = generateRecentMonths(parseInt(monthsToShow));
        checkDataAvailability(months);
    }, [monthsToShow, clinicSeatBillingApi]);

    // Filter months to only show ones with data
    const availableMonths = recentMonths.filter(month => monthsWithData.has(month.period));

    // Handle CSV download - simplified since we only show months with data
    const handleDownload = async (period: string) => {
        setDownloadingMonths(prev => new Set([...prev, period]));
        
        try {
            await clinicSeatBillingApi.exportBillingReportCSV(period);
            // Success notification handled by the API
        } catch (error) {
            console.error('Download failed for period:', period, error);
            // Error notification handled by the API
        } finally {
            setDownloadingMonths(prev => {
                const newSet = new Set(prev);
                newSet.delete(period);
                return newSet;
            });
        }
    };

    return (
        <Stack gap="lg">
            {/* Header */}
            <div>
                <Title order={4} mb="xs">
                    <IconBuildingHospital size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    {t('clinicSeatBilling.tab.title')}
                </Title>
                <Text c="dimmed" size="sm">
                    {t('clinicSeatBilling.tab.description')}
                </Text>
            </div>

            {/* Month Selection */}
            <Group>
                <Text size="sm" fw={500}>{t('clinicSeatBilling.tab.showRecent')}</Text>
                <Select
                    data={[
                        { value: '3', label: t('clinicSeatBilling.tab.periodOptions.3months') },
                        { value: '6', label: t('clinicSeatBilling.tab.periodOptions.6months') },
                        { value: '12', label: t('clinicSeatBilling.tab.periodOptions.12months') },
                        { value: '24', label: t('clinicSeatBilling.tab.periodOptions.24months') }
                    ]}
                    value={monthsToShow}
                    onChange={(value) => value && setMonthsToShow(value)}
                    w={120}
                />
            </Group>

            {/* Months List */}
            {checkingData ? (
                <Center py="xl">
                    <Stack align="center" gap="md">
                        <Loader size="md" />
                        <Text c="dimmed" size="sm">
                            {t('clinicSeatBilling.tab.checking')}
                        </Text>
                    </Stack>
                </Center>
            ) : (
                <Stack gap="sm">
                    {availableMonths.map((month) => (
                        <Paper key={month.period} p="md" withBorder>
                            <Group justify="space-between" align="center">
                                <div>
                                    <Group gap="xs" align="center">
                                        <IconCalendar size={16} color="gray" />
                                        <Text fw={500}>{month.displayName}</Text>
                                        <Text size="sm" c="dimmed">({month.period})</Text>
                                    </Group>
                                </div>
                                
                                <Button
                                    leftSection={<IconDownload size={16} />}
                                    size="sm"
                                    variant="light"
                                    loading={downloadingMonths.has(month.period)}
                                    onClick={() => handleDownload(month.period)}
                                >
                                    {t('clinicSeatBilling.tab.downloadCsv')}
                                </Button>
                            </Group>
                        </Paper>
                    ))}
                </Stack>
            )}

            {!checkingData && availableMonths.length === 0 && (
                <Text c="dimmed" ta="center" py="xl">
                    {t('clinicSeatBilling.tab.noDataMessage')}
                </Text>
            )}
        </Stack>
    );
};

export default ClinicSeatBillingTab;

